/** Eigenes Möbel aus Quadern, Zylindern und Kugeln zusammenbauen. Eingaben in cm. */
import { useEffect, useRef, useState } from "react";
import type { CustomDef } from "@hausplaner/model";
import { useStore, closeSheet, saveCustom, deleteCustom, toast } from "../state/store";
import { renderModel } from "../engine/thumbs";
import { placeFromView } from "./Catalog";

type Shape = "box" | "cyl" | "sph";
interface PartCm { s: Shape; w: number; d: number; h: number; x: number; z: number; y: number; c: string }

const TPL: Record<string, () => PartCm[]> = {
  box: () => [{ s: "box", w: 60, d: 60, h: 60, x: 0, z: 0, y: 0, c: "#e9e5dc" }],
  cab: () => [{ s: "box", w: 100, d: 58, h: 200, x: 0, z: 0, y: 0, c: "#e9e5dc" }, { s: "box", w: 0.6, d: 1, h: 196, x: 0, z: 29.5, y: 2, c: "#232527" }, { s: "box", w: 2, d: 2.5, h: 25, x: -6, z: 30, y: 95, c: "#c3c7ca" }, { s: "box", w: 2, d: 2.5, h: 25, x: 6, z: 30, y: 95, c: "#c3c7ca" }],
  shelf: () => { const P: PartCm[] = [{ s: "box", w: 2, d: 35, h: 180, x: -39, z: 0, y: 0, c: "#c9a272" }, { s: "box", w: 2, d: 35, h: 180, x: 39, z: 0, y: 0, c: "#c9a272" }]; for (let i = 0; i < 5; i++) P.push({ s: "box", w: 76, d: 35, h: 2, x: 0, z: 0, y: i * 44.5, c: "#c9a272" }); return P; },
  table: () => [{ s: "box", w: 140, d: 80, h: 4, x: 0, z: 0, y: 71, c: "#c9a272" }, ...[[-65, -35], [65, -35], [-65, 35], [65, 35]].map(([x, z]) => ({ s: "box" as Shape, w: 5, d: 5, h: 71, x, z, y: 0, c: "#232527" }))],
  round: () => [{ s: "cyl", w: 80, d: 80, h: 4, x: 0, z: 0, y: 71, c: "#e9e5dc" }, { s: "cyl", w: 8, d: 8, h: 71, x: 0, z: 0, y: 0, c: "#232527" }, { s: "cyl", w: 50, d: 50, h: 3, x: 0, z: 0, y: 0, c: "#232527" }],
  bench: () => [{ s: "box", w: 120, d: 35, h: 5, x: 0, z: 0, y: 40, c: "#8a6648" }, { s: "box", w: 5, d: 30, h: 40, x: -52, z: 0, y: 0, c: "#8a6648" }, { s: "box", w: 5, d: 30, h: 40, x: 52, z: 0, y: 0, c: "#8a6648" }],
};
const PF: [keyof PartCm, string][] = [["w", "Breite"], ["d", "Tiefe"], ["h", "Höhe"], ["x", "← links / rechts →"], ["z", "↑ hinten / vorne ↓"], ["y", "über Boden"]];
const cmv = (v: number) => Math.round(v * 100);

/** Teile in cm → Möbel in Metern, zentriert, Unterkante auf 0 */
function partsToDef(P: PartCm[]): Omit<CustomDef, "key" | "name"> {
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity, y0 = Infinity, y1 = -Infinity;
  for (const p of P) {
    x0 = Math.min(x0, (p.x - p.w / 2) / 100); x1 = Math.max(x1, (p.x + p.w / 2) / 100);
    z0 = Math.min(z0, (p.z - p.d / 2) / 100); z1 = Math.max(z1, (p.z + p.d / 2) / 100);
    y0 = Math.min(y0, p.y / 100); y1 = Math.max(y1, (p.y + p.h) / 100);
  }
  const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;
  const parts = P.map((p) => ({ s: p.s, w: p.w / 100, d: p.d / 100, h: p.h / 100, x: +(p.x / 100 - cx).toFixed(4), z: +(p.z / 100 - cz).toFixed(4), y: +(p.y / 100 - y0).toFixed(4), c: p.c }));
  return { w: +(x1 - x0).toFixed(4), d: +(z1 - z0).toFixed(4), h: +(y1 - y0).toFixed(4), c: parts[0].c, parts };
}
const valid = (P: PartCm[]) => P.filter((p) => p.w > 0 && p.d > 0 && p.h > 0 && [p.x, p.y, p.z].every(isFinite));

export function BuilderSheet() {
  const open = useStore((s) => s.sheet === "builder");
  if (!open) return null;
  return <Builder />;
}

function Builder() {
  const key = useStore((s) => s.builderKey);
  const custom = useStore((s) => s.custom);
  const items = useStore((s) => s.items);
  const existing = key ? custom.find((c) => c.key === key) : undefined;
  const [name, setName] = useState(existing?.name || "");
  const [parts, setParts] = useState<PartCm[]>(() => existing
    ? existing.parts.map((p) => ({ s: p.s, w: cmv(p.w), d: cmv(p.d), h: cmv(p.h), x: cmv(p.x), z: cmv(p.z), y: cmv(p.y), c: p.c }))
    : TPL.box());
  const [tpl, setTpl] = useState("");
  const [rev, setRev] = useState(0); // Eingabefelder neu aufbauen, wenn sich die Teile-Liste umbaut
  const [confirmDel, setConfirmDel] = useState(false);
  const [angle, setAngle] = useState(0.7);
  const cv = useRef<HTMLCanvasElement>(null);
  const drag = useRef<number | null>(null);
  const P = valid(parts);
  const def = P.length ? partsToDef(P) : null;

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const c = cv.current; if (!c) return; const g = c.getContext("2d")!; g.clearRect(0, 0, c.width, c.height);
      if (!def) return;
      const src = renderModel({ id: "custom", w: def.w, d: def.d, h: def.h, c: def.c, parts: def.parts }, c.width, c.height, angle);
      if (src) g.drawImage(src, 0, 0);
    });
    return () => cancelAnimationFrame(raf);
  });

  const upd = (i: number, patch: Partial<PartCm>) => setParts((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const addPart = (s: Shape) => { setRev((r) => r + 1); setParts((ps) => { const last = ps[ps.length - 1]; return [...ps, { s, w: 40, d: 40, h: 40, x: 0, z: 0, y: last ? last.y + last.h : 0, c: "#c9a272" }]; }); };
  const save = () => {
    if (!def) return toast("Bitte mindestens ein Teil mit Breite, Tiefe und Höhe angeben");
    const created = saveCustom({ ...def, name: name.trim() || "Eigenes Möbel" }, existing ? existing.key : null);
    if (created) placeFromView(created);
  };
  const del = () => {
    if (!existing) return;
    if (!confirmDel) { setConfirmDel(true); return; }
    deleteCustom(existing.key);
  };
  const nUsed = existing ? items.filter((i) => i.k === existing.key).length : 0;

  return (
    <section className="sheet wide" id="builder" aria-label="Eigenes Möbel bauen">
      <div className="sh-head"><h2 id="bd-title">{existing ? "Eigenes Möbel bearbeiten" : "Eigenes Möbel bauen"}</h2><button className="x" onClick={closeSheet} aria-label="Schließen">×</button></div>
      <div className="sh-body">
        <div className="bd-top">
          <canvas id="bd-prev" ref={cv} width={360} height={260} aria-label="Vorschau, zum Drehen ziehen"
            onPointerDown={(e) => { drag.current = e.clientX; e.currentTarget.setPointerCapture(e.pointerId); }}
            onPointerMove={(e) => { if (drag.current == null) return; const dx = e.clientX - drag.current; drag.current = e.clientX; setAngle((a) => a + dx * 0.012); }}
            onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }} />
          <div className="bd-meta">
            <label className="field" htmlFor="bd-name"><span>Name</span><input id="bd-name" type="text" maxLength={40} placeholder="z. B. Werkbank Papa" value={name} onChange={(e) => setName(e.target.value)} /></label>
            <label className="field" htmlFor="bd-tpl"><span>Vorlage laden</span>
              <select id="bd-tpl" value={tpl} onChange={(e) => { const v = e.target.value; setTpl(v); if (TPL[v]) { setParts(TPL[v]()); setRev((r) => r + 1); } }}>
                <option value="">– auswählen –</option><option value="box">Einzelner Quader</option><option value="cab">Schrank</option><option value="shelf">Regal</option>
                <option value="table">Tisch</option><option value="round">Runder Tisch</option><option value="bench">Bank</option>
              </select>
            </label>
            <p className="meta" id="bd-size">{def ? `Gesamt: ${cmv(def.w)} × ${cmv(def.d)} × ${cmv(def.h)} cm (B × T × H)` : "Mindestens ein Teil mit Maßen > 0 nötig"}</p>
            <p className="note">Ein Möbel besteht aus Teilen. Position in cm von der Mitte aus, „über Boden“ ist die Unterkante. Vorschau zum Drehen ziehen.</p>
          </div>
        </div>
        <div id="bd-parts" className="parts">
          {parts.map((p, i) => (
            <div className="part" key={`${rev}-${i}`}>
              <div className="row">
                <b>Teil {i + 1}</b>
                <select aria-label="Form" value={p.s} onChange={(e) => upd(i, { s: e.target.value as Shape })}>
                  <option value="box">Quader</option><option value="cyl">Zylinder</option><option value="sph">Kugel</option>
                </select>
                <input type="color" value={p.c} aria-label={"Farbe Teil " + (i + 1)} onChange={(e) => upd(i, { c: e.target.value })} />
                <button className="btn small" type="button" onClick={() => { setRev((r) => r + 1); setParts((ps) => [...ps.slice(0, i + 1), { ...p, x: p.x + 10 }, ...ps.slice(i + 1)]); }}>Kopie</button>
                <button className="btn small danger" type="button" disabled={parts.length < 2} onClick={() => { setRev((r) => r + 1); setParts((ps) => ps.filter((_, j) => j !== i)); }}>Entfernen</button>
              </div>
              <div className="dims dims6">
                {PF.map(([k, l]) => (
                  <label key={k}>{l} cm
                    <input type="number" inputMode="decimal" step="1" min={"wdh".includes(k) ? "0.5" : undefined} defaultValue={p[k] as number}
                      key={`${rev}-${i}-${k}`}
                      onChange={(e) => { const v = parseFloat(e.target.value); if (isFinite(v)) upd(i, { [k]: v } as Partial<PartCm>); }} />
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="row">
          <button className="btn small" id="bd-add" onClick={() => addPart("box")}>+ Quader</button>
          <button className="btn small" id="bd-addc" onClick={() => addPart("cyl")}>+ Zylinder</button>
          <button className="btn small" id="bd-adds" onClick={() => addPart("sph")}>+ Kugel</button>
        </div>
        <div className="row">
          <button className="btn primary" id="bd-save" onClick={save}>{existing ? "Änderungen speichern" : "Speichern & platzieren"}</button>
          {existing && <button className="btn small danger" id="bd-del" onClick={del}>{confirmDel ? (nUsed ? `Wirklich löschen? (${nUsed}× im Haus)` : "Wirklich löschen?") : "Aus Katalog löschen"}</button>}
        </div>
      </div>
    </section>
  );
}
